import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { CarrierVisitDirection } from '@yard/app/api'
import { AllocationRuleForm } from '@yard/components/AllocationRules/AllocationRuleForm'
import { AllocationRuleFormStore } from '@yard/components/AllocationRules/stores/allocation-rule.form-store'
import { useAllocationRuleTemplateMessageHandler } from '@yard/pages/AllocationRulesTemplate/hooks/useAllocationRuleTemplateMessageHandler'
import { IBaseAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/base-allocation-rules.view-store'
import { CarrierVisitAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import { YardViewStore } from '@yard/pages/YardSetup/stores/yard.view-store'
import { generateAllocationRuleId } from '@yard/utils/allocation-rules.util'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'

interface Props {
  yardViewStore: YardViewStore
  allocationRulesViewStore: IBaseAllocationRulesViewStore
}
export const AllocationRuleDialogCreate: FC<Props> = observer(props => {
  const { yardViewStore, allocationRulesViewStore } = props
  const { t } = useTranslate()
  const { getSuccessMessage } = useAllocationRuleTemplateMessageHandler()

  const isEditing = allocationRulesViewStore.isEditing
  const editingRule = allocationRulesViewStore.editingRule

  const formStore = useMemo(() => new AllocationRuleFormStore(), [])

  useEffect(() => {
    if (isEditing && editingRule) {
      formStore.setValues(editingRule)
    } else if (yardViewStore.createRuleFromFilter) {
      formStore.setValues({
        id: generateAllocationRuleId(),
        name: '',
        isTemplate: false,
        destinations: [],
        priority: 0,
        facets: {
          ...yardViewStore.filters,
          containerOperatorId: yardViewStore.filters.containerOperatorId,
          containerHeight: yardViewStore.filters.containerHeight?.[0],
          containerType: yardViewStore.filters.containerType?.[0],
          size: yardViewStore.filters.containerLength?.[0],
          direction: CarrierVisitDirection.Outbound,
        },
      })
    } else {
      let direction: CarrierVisitDirection = CarrierVisitDirection.Outbound
      let carrierVisitId: number | undefined
      let carrierType: any

      if (allocationRulesViewStore instanceof CarrierVisitAllocationRulesViewStore) {
        direction =
          allocationRulesViewStore.selectedCarrierVisitDirection ?? CarrierVisitDirection.Outbound
        carrierVisitId = allocationRulesViewStore.selectedCarrierVisitId
        carrierType = allocationRulesViewStore.selectedCarrierVisit?.carrierType
      }

      formStore.setValues({
        id: generateAllocationRuleId(),
        name: '',
        isTemplate: false,
        destinations: [],
        priority: 0,
        facets: {
          direction,
          carrierVisitId,
          carrierType,
        },
      })
    }
  }, [
    formStore,
    allocationRulesViewStore,
    allocationRulesViewStore.isFormDialogOpen,
    isEditing,
    editingRule,
    yardViewStore.createRuleFromFilter,
    yardViewStore.filters,
  ])

  const onSubmit = async () => {
    if (formStore.data.destinations.length === 0) {
      appStore.setShowAlert(
        'error',
        t('allocationRuleDestinationRequired', 'At least one destination is required'),
      )
      return
    }

    if (formStore.validate()) {
      allocationRulesViewStore.upsertRuleFromFormData(formStore.data)
      appStore.setShowAlert('success', getSuccessMessage(isEditing))
      allocationRulesViewStore.toggleFormDialog()
      yardViewStore.setCreateRuleFromFilter(false)
    }
  }

  return (
    <>
      <ConfirmationDialog
        open={allocationRulesViewStore.isFormDialogOpen}
        title={
          isEditing
            ? t('editAllocationRule', 'Edit allocation rule')
            : t('createAllocationRule', 'Create allocation rule')
        }
        primaryActionText={
          isEditing ? t('yesUpdate', 'Yes, update') : t('yesCreate', 'Yes, create')
        }
        closeLabel={t('noCancel', 'No, cancel')}
        onConfirm={async () => await onSubmit()}
        onClose={() => {
          allocationRulesViewStore.toggleFormDialog()
          yardViewStore.setCreateRuleFromFilter(false)
        }}
        formId='allocation-rule-form'
        maxWidth='md'
      >
        <AllocationRuleForm
          formStore={formStore}
          disableDirectionChange={
            allocationRulesViewStore instanceof CarrierVisitAllocationRulesViewStore
          }
          disableTransportSelection={
            allocationRulesViewStore instanceof CarrierVisitAllocationRulesViewStore
          }
          transportSelection={
            allocationRulesViewStore instanceof CarrierVisitAllocationRulesViewStore
              ? yardViewStore.transportSelection
              : undefined
          }
          companyAutoComplete={yardViewStore.companyAutoComplete}
          locationCodeAutoComplete={yardViewStore.locationCodeAutoComplete}
          imoClassAutoComplete={yardViewStore.imoClassAutoComplete}
        />
      </ConfirmationDialog>
    </>
  )
})
